package ai.jessie.web.projects

import ai.jessie.web.theme.ChoiceCardStyle
import ai.jessie.web.theme.ChoiceCardsStyle
import ai.jessie.web.theme.ChoiceHintStyle
import ai.jessie.web.theme.FieldErrorStyle
import ai.jessie.web.theme.FieldInputStyle
import ai.jessie.web.theme.FieldOptionalStyle
import ai.jessie.web.theme.FieldStyle
import ai.jessie.web.theme.FieldsetStyle
import ai.jessie.web.theme.LabelStyle
import ai.jessie.web.theme.NumInputStyle
import ai.jessie.web.theme.RowStyle
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.forId
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Legend
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

// `name` is the server's field path, so a returned problem marks exactly this control.
internal data class ProjectField(
    val name: String,
    val label: String,
    val value: String,
    val problem: String? = null,
)

/** One selectable card of [projectChoice]: the stored value, its title and its description. */
internal data class ProjectChoice(val value: String, val title: String, val description: String)

@Composable
internal fun projectField(field: ProjectField, decimal: Boolean = false, change: (String) -> Unit) {
    val id = "project-${field.name}"
    fieldBlock(field, id) {
        Input(
            InputType.Text,
            attrs =
                (if (decimal) NumInputStyle else FieldInputStyle).toAttrs {
                    id(id)
                    value(field.value)
                    // Exact decimal text; a number input would round-trip through floating point.
                    if (decimal) attr("inputmode", "decimal")
                    fieldAttributes(field)
                    onInput { change(it.value) }
                },
        )
    }
}

@Composable
internal fun projectSelect(
    field: ProjectField,
    options: Map<String, String>,
    emptyLabel: String = "Bitte wählen",
    change: (String) -> Unit,
) {
    val id = "project-${field.name}"
    fieldBlock(field, id) {
        Select(
            attrs =
                FieldInputStyle.toAttrs {
                    id(id)
                    fieldAttributes(field)
                    onChange { change(it.value.orEmpty()) }
                }
        ) {
            (mapOf("" to emptyLabel) + options).forEach { (value, label) ->
                Option(value, attrs = { if (value == field.value) selected() }) { Text(label) }
            }
        }
    }
}

/**
 * Radio choices as selectable cards (`docs/design/components.md`). The fieldset carries the field's
 * ID, so a returned problem focuses and marks the whole group; each radio is `<id>-<value>`.
 */
@Composable
internal fun projectChoice(
    field: ProjectField,
    choices: List<ProjectChoice>,
    change: (String) -> Unit,
) {
    val id = "project-${field.name}"
    Fieldset(
        attrs =
            FieldsetStyle.toAttrs {
                id(id)
                attr("tabindex", "-1")
                attr("role", "radiogroup")
                if (field.problem != null) {
                    attr("aria-invalid", "true")
                    attr("aria-describedby", "project-problems")
                }
            }
    ) {
        Legend(attrs = LabelStyle.toAttrs { style { property("margin-bottom", "8px") } }) {
            Text(field.label)
        }
        Div(attrs = ChoiceCardsStyle.toAttrs()) {
            choices.forEach { choice ->
                val choiceId = "$id-${choice.value}"
                Label(attrs = ChoiceCardStyle.toAttrs { forId(choiceId) }) {
                    Input(
                        InputType.Radio,
                        attrs = {
                            id(choiceId)
                            attr("name", field.name)
                            value(choice.value)
                            checked(choice.value == field.value)
                            onChange { change(choice.value) }
                        },
                    )
                    Span {
                        Text(choice.title)
                        Span(attrs = ChoiceHintStyle.toAttrs()) { Text(choice.description) }
                    }
                }
            }
        }
        field.problem?.let { P(attrs = FieldErrorStyle.toAttrs()) { Text(it) } }
    }
}

@Composable
internal fun projectCheckbox(
    name: String,
    label: String,
    value: Boolean,
    change: (Boolean) -> Unit,
) {
    val id = "project-$name"
    Div(attrs = RowStyle.toAttrs()) {
        Input(
            InputType.Checkbox,
            attrs = {
                id(id)
                attr("name", name)
                checked(value)
                onChange { change(it.value) }
            },
        )
        Label(attrs = { forId(id) }) { Text(label) }
    }
}

// The label text is unchanged; a trailing parenthesis such as "(optional)" is rendered in the
// quieter suffix style.
@Composable
private fun fieldBlock(field: ProjectField, id: String, control: @Composable () -> Unit) {
    Div(attrs = FieldStyle.toAttrs()) {
        Label(attrs = { forId(id) }) {
            val hint = field.label.indexOf(" (")
            if (hint < 0) Text(field.label)
            else {
                Text(field.label.substring(0, hint + 1))
                Span(attrs = FieldOptionalStyle.toAttrs()) { Text(field.label.substring(hint + 1)) }
            }
        }
        control()
        field.problem?.let { P(attrs = FieldErrorStyle.toAttrs()) { Text(it) } }
    }
}

private fun AttrsScope<*>.fieldAttributes(field: ProjectField) {
    attr("name", field.name)
    attr("autocomplete", "off")
    if (field.problem != null) {
        attr("aria-invalid", "true")
        attr("aria-describedby", "project-problems")
    }
}
